import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import GooglePlacesAutocomplete, { geocodeByPlaceId } from "react-google-places-autocomplete";
import stateOptions from "./stateOptions";
import {
  ADDRESS_PRIMARY,
  ADDRESS_PRIMARY_HOVER,
  GOOGLE_PLACES_API_KEY,
  addressFromInitial,
  parseAddressComponents,
} from "../utils/addressUtils";

/**
 * Shared address entry UI: Google Places search + manual fallback.
 * Used on account-details, take-action campaigns, and similar flows.
 */
export default function AddressEntryForm({
  hint = null,
  submitLabel = "Continue",
  onSubmit,
  loading = false,
  externalError = null,
  initialAddress = null,
  submitAlign = "right",
  hintColor = "#475569",
}) {
  const hasApiKey = !!GOOGLE_PLACES_API_KEY;

  const [useManualEntry, setUseManualEntry] = useState(!hasApiKey);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const activePlaceRequestRef = useRef(0);
  const [address, setAddress] = useState(() => addressFromInitial(initialAddress));

  useEffect(() => {
    setAddress(addressFromInitial(initialAddress));
    setSelectedPlace(null);
    setInternalError(null);
    setUseManualEntry(!hasApiKey);
    setResolvingPlace(false);
    activePlaceRequestRef.current = 0;
  }, [
    initialAddress?.street_1,
    initialAddress?.city,
    initialAddress?.state,
    initialAddress?.zipcode,
    hasApiKey,
  ]);

  const handleField = (field) => (e) => {
    const value = e?.target?.value ?? "";
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const clearAddressFields = useCallback(() => {
    setAddress({ street_1: "", city: "", state: "", zipcode: "" });
  }, []);

  const handlePlaceSelection = useCallback(async (placeOption) => {
    setSelectedPlace(placeOption);
    const placeId = placeOption?.value?.place_id;
    if (!placeId) {
      activePlaceRequestRef.current += 1;
      setResolvingPlace(false);
      clearAddressFields();
      setInternalError(null);
      return;
    }
    const requestId = activePlaceRequestRef.current + 1;
    activePlaceRequestRef.current = requestId;
    setResolvingPlace(true);
    try {
      const geocodeResults = await geocodeByPlaceId(placeId);
      const parsed = parseAddressComponents(geocodeResults?.[0]);
      if (parsed && requestId === activePlaceRequestRef.current) {
        setAddress((prev) => ({
          street_1: parsed.street_1 || prev.street_1,
          city: parsed.city || prev.city,
          state: (parsed.state || prev.state || "").toUpperCase(),
          zipcode: parsed.zipcode || prev.zipcode,
        }));
        setInternalError(null);
      }
    } catch {
      if (requestId === activePlaceRequestRef.current) {
        setInternalError("Unable to load address details. Please try another address.");
      }
    } finally {
      if (requestId === activePlaceRequestRef.current) {
        setResolvingPlace(false);
      }
    }
  }, [clearAddressFields]);

  const normalizedState = (address.state || "").trim().toUpperCase();
  const normalizedZip = (address.zipcode || "").trim();
  const isZipValid = /^\d{5}(-\d{4})?$/.test(normalizedZip);
  const isStateValid = !!stateOptions.find((opt) => opt.value === normalizedState);

  const isAddressComplete = useMemo(
    () =>
      !!(
        address.street_1?.trim() &&
        address.city?.trim() &&
        normalizedState &&
        normalizedZip &&
        isStateValid &&
        isZipValid
      ),
    [address, normalizedState, normalizedZip, isStateValid, isZipValid]
  );

  const displayError = externalError || internalError;
  const isBusy = loading || resolvingPlace;

  const handleSubmit = async () => {
    if (!isAddressComplete || isBusy || typeof onSubmit !== "function") return;
    setInternalError(null);
    await onSubmit({
      street_1: address.street_1.trim(),
      city: address.city.trim(),
      state: normalizedState,
      zipcode: normalizedZip,
    });
  };

  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <Box>
      {hint ? (
        <Typography variant="body2" sx={{ color: hintColor, mb: 2 }}>
          {hint}
        </Typography>
      ) : null}

      {displayError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {displayError}
        </Alert>
      ) : null}

      {hasApiKey ? (
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={useManualEntry ? "manual" : "search"}
          onChange={(_, value) => {
            if (!value) return;
            setUseManualEntry(value === "manual");
          }}
          aria-label="Address entry mode"
          sx={{
            mb: 2,
            borderRadius: "12px",
            overflow: "hidden",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              py: 0.65,
              minHeight: 36,
              color: "#334155",
              borderColor: "#e2e8f0",
              borderRadius: 0,
              "&.Mui-selected": {
                bgcolor: ADDRESS_PRIMARY,
                color: "#fff",
                "&:hover": { bgcolor: ADDRESS_PRIMARY_HOVER },
              },
            },
            "& .MuiToggleButton-root:first-of-type": {
              borderRadius: "12px 0 0 12px",
            },
            "& .MuiToggleButton-root:last-of-type": {
              borderRadius: "0 12px 12px 0",
            },
          }}
        >
          <ToggleButton value="search" aria-label="Search address">
            <SearchIcon sx={{ fontSize: 16, mr: 0.75 }} />
            Search Address
          </ToggleButton>
          <ToggleButton value="manual" aria-label="Enter manually">
            <EditIcon sx={{ fontSize: 16, mr: 0.75 }} />
            Enter Manually
          </ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      {!hasApiKey ? (
        <Typography variant="caption" sx={{ display: "block", mb: 2, color: "#64748b" }}>
          Manual entry only. (Address search is unavailable in this environment.)
        </Typography>
      ) : null}

      {!useManualEntry && hasApiKey ? (
        <Box sx={{ mb: 1 }}>
          <GooglePlacesAutocomplete
            apiKey={GOOGLE_PLACES_API_KEY}
            autocompletionRequest={{
              componentRestrictions: { country: "us" },
              types: ["address"],
            }}
            debounce={300}
            selectProps={{
              value: selectedPlace,
              onChange: handlePlaceSelection,
              placeholder: "Start typing your address...",
              isClearable: true,
              isDisabled: isBusy,
              isLoading: resolvingPlace,
              menuPortalTarget,
              menuPosition: "fixed",
              onInputChange: () => {
                if (internalError) setInternalError(null);
              },
              styles: {
                control: (provided, state) => ({
                  ...provided,
                  minHeight: 48,
                  borderRadius: 12,
                  borderColor: state.isFocused ? ADDRESS_PRIMARY : "#e2e8f0",
                  boxShadow: state.isFocused ? `0 0 0 1px ${ADDRESS_PRIMARY}` : "none",
                  "&:hover": { borderColor: state.isFocused ? ADDRESS_PRIMARY : "#cbd5e1" },
                }),
                placeholder: (provided) => ({ ...provided, color: "#94a3b8" }),
                menuPortal: (provided) => ({ ...provided, zIndex: 1500 }),
                menu: (provided) => ({
                  ...provided,
                  zIndex: 1500,
                  borderRadius: 12,
                  overflow: "hidden",
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? "#f1f5f9" : "#fff",
                  color: "#0f172a",
                  paddingTop: 10,
                  paddingBottom: 10,
                }),
              },
            }}
          />

          {(address.street_1 || address.city || address.state || address.zipcode) ? (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${ADDRESS_PRIMARY}33`,
                bgcolor: `${ADDRESS_PRIMARY}0d`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                Selected address
              </Typography>
              <Typography variant="body2" sx={{ color: "#0f172a" }}>
                {address.street_1}
              </Typography>
              <Typography variant="body2" sx={{ color: "#0f172a" }}>
                {[address.city, address.state, address.zipcode].filter(Boolean).join(", ")}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  clearAddressFields();
                  setSelectedPlace(null);
                }}
                sx={{
                  mt: 1,
                  p: 0,
                  minWidth: 0,
                  textTransform: "none",
                  fontWeight: 600,
                  color: ADDRESS_PRIMARY,
                  "&:hover": { bgcolor: "transparent", color: ADDRESS_PRIMARY_HOVER },
                }}
              >
                Clear
              </Button>
            </Box>
          ) : null}
        </Box>
      ) : (
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Street Address"
            value={address.street_1}
            onChange={handleField("street_1")}
            variant="outlined"
            size="small"
            autoComplete="street-address"
          />
          <TextField
            fullWidth
            label="City"
            value={address.city}
            onChange={handleField("city")}
            variant="outlined"
            size="small"
            autoComplete="address-level2"
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              select
              label="State"
              value={normalizedState}
              onChange={handleField("state")}
              variant="outlined"
              size="small"
              error={Boolean(address.state) && !isStateValid}
              helperText={Boolean(address.state) && !isStateValid ? "Select a valid U.S. state." : " "}
            >
              <MenuItem value="">
                <em>Select State</em>
              </MenuItem>
              {stateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Zip Code"
              value={address.zipcode}
              onChange={handleField("zipcode")}
              variant="outlined"
              size="small"
              autoComplete="postal-code"
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              error={Boolean(address.zipcode) && !isZipValid}
              helperText={Boolean(address.zipcode) && !isZipValid ? "Use ZIP format 12345 or 12345-6789." : " "}
            />
          </Stack>
        </Stack>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent:
            submitAlign === "left"
              ? "flex-start"
              : submitAlign === "center"
                ? "center"
                : "flex-end",
          mt: 2,
        }}
      >
        <Button
          onClick={handleSubmit}
          disabled={!isAddressComplete || isBusy}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            minHeight: 44,
            borderRadius: "12px",
            bgcolor: ADDRESS_PRIMARY,
            "&:hover": { bgcolor: ADDRESS_PRIMARY_HOVER },
            "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : submitLabel}
        </Button>
      </Box>
    </Box>
  );
}
